import React, { useEffect, useState } from 'react';
import axios from '@/lib/axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
    Loader2,
    Phone,
    Building2,
    User,
    Calendar,
    ArrowLeftRight,
    GitBranch,
    Users,
    CheckCircle2,
    XCircle,
    Clock,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface JourneyUser {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    phone_number: string | null;
    phone_from_individual: boolean;
    is_staff: boolean;
    is_superuser: boolean;
    business_name: string | null;
    bap_name: string | null;
    is_whitespace: boolean | null;
    crm_lead_id: number | null;
    journey_stage: string;
    journey_index: string;
    journey_updated_at: string | null;
}

interface LeadJourneySwapData {
    direct_journeys: JourneyUser[];
    b2p_journeys: JourneyUser[];
}

interface LeadJourneySwapModalProps {
    phone: string | null;
    tradeName?: string;
    currentLeadId?: number | null;
    isOpen: boolean;
    onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string | null): string => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    } catch {
        return iso;
    }
};

const stageColorMap: Record<string, string> = {
    '-': 'bg-gray-100 text-gray-500 border-gray-200',
    consent: 'bg-blue-50 text-blue-600 border-blue-200',
    business_info: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    loan_amount: 'bg-violet-50 text-violet-600 border-violet-200',
    internal_rules: 'bg-amber-50 text-amber-600 border-amber-200',
    success: 'bg-green-50 text-green-700 border-green-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
};

function getStageClass(stage: string): string {
    const key = (stage || '-').toLowerCase().replace(/\s+/g, '_');
    return stageColorMap[key] ?? 'bg-purple-50 text-purple-600 border-purple-200';
}

// ─── Journey User Card ────────────────────────────────────────────────────────

const JourneyUserCard: React.FC<{ user: JourneyUser; currentLeadId?: number | null; highlight?: boolean }> = ({
    user,
    currentLeadId,
    highlight = false,
}) => {
    const fullName =
        [user.first_name, user.last_name].filter(Boolean).join(' ') || null;
    const isLinked = currentLeadId != null && user.crm_lead_id === currentLeadId;

    return (
        <div
            className={`rounded-xl border bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden ${
                isLinked
                    ? 'border-yellow-400 hover:border-yellow-500 shadow-yellow-100'
                    : highlight
                    ? 'border-intellend-green/40 hover:border-intellend-green/60'
                    : 'border-gray-100 hover:border-gray-200'
            }`}
        >
            {/* Header */}
            <div
                className={`flex items-start justify-between px-4 py-3 border-b ${
                    isLinked
                        ? 'bg-yellow-50/60 border-yellow-200/60'
                        : highlight
                        ? 'bg-intellend-green/5 border-intellend-green/10'
                        : 'bg-gray-50/60 border-gray-100'
                }`}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-gray-900 truncate">
                            {fullName ?? (
                                <span className="italic text-gray-400 font-normal">No name</span>
                            )}
                        </span>
                        {user.business_name && (
                            <span className="text-xs text-gray-400 italic truncate">
                                ({user.business_name})
                            </span>
                        )}
                        {isLinked && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-300 shrink-0">
                                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </svg>
                                Linked
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="text-[10px] font-mono text-gray-400">#{user.id}</span>
                        {user.bap_name && (
                            <>
                                <span className="text-gray-300">·</span>
                                <span className="text-[10px] text-intellend-green font-semibold">
                                    {user.bap_name}
                                </span>
                            </>
                        )}
                        {user.is_whitespace && (
                            <>
                                <span className="text-gray-300">·</span>
                                <span className="text-[10px] text-blue-500 font-semibold">WS</span>
                            </>
                        )}
                        {isLinked && (
                            <>
                                <span className="text-gray-300">·</span>
                                <span className="text-[10px] font-mono text-yellow-600">
                                    CRM #{user.crm_lead_id}
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <Badge
                    variant="outline"
                    className={`text-[10px] font-semibold ml-2 shrink-0 ${getStageClass(user.journey_stage)}`}
                >
                    {user.journey_stage || '-'}
                </Badge>
            </div>

            {/* Body */}
            <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {/* Phone */}
                <div className="flex items-start gap-2">
                    <Phone className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                            Phone
                        </p>
                        <p
                            className={`text-xs font-mono ${
                                user.phone_from_individual ? 'text-gray-700' : 'text-amber-600'
                            }`}
                            title={
                                user.phone_from_individual
                                    ? undefined
                                    : 'Phone from lead mapping (no IndividualDetail)'
                            }
                        >
                            {user.phone_number || '-'}
                        </p>
                    </div>
                </div>

                {/* Username */}
                <div className="flex items-start gap-2">
                    <User className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                            Username
                        </p>
                        <p className="text-xs font-mono text-gray-600 truncate">{user.username}</p>
                    </div>
                </div>

                {/* Journey Progress */}
                <div className="flex items-start gap-2">
                    <GitBranch className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                            Progress
                        </p>
                        <p className="text-xs text-gray-700 font-mono">{user.journey_index}</p>
                    </div>
                </div>

                {/* Last Updated */}
                <div className="flex items-start gap-2">
                    <Clock className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                            Last Updated
                        </p>
                        <p className="text-xs text-gray-700">
                            {formatDate(user.journey_updated_at)}
                        </p>
                    </div>
                </div>

                {/* Flags */}
                <div className="flex items-center gap-3 sm:col-span-2 mt-0.5">
                    <span
                        className={`flex items-center gap-1 text-[10px] font-semibold ${
                            user.is_staff ? 'text-green-600' : 'text-gray-300'
                        }`}
                    >
                        {user.is_staff ? (
                            <CheckCircle2 className="h-3 w-3" />
                        ) : (
                            <XCircle className="h-3 w-3" />
                        )}
                        Admin
                    </span>
                    <span
                        className={`flex items-center gap-1 text-[10px] font-semibold ${
                            user.is_superuser ? 'text-green-600' : 'text-gray-300'
                        }`}
                    >
                        {user.is_superuser ? (
                            <CheckCircle2 className="h-3 w-3" />
                        ) : (
                            <XCircle className="h-3 w-3" />
                        )}
                        Superuser
                    </span>
                </div>
            </div>
        </div>
    );
};

// ─── Section ──────────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    count: number;
    accent?: string;
}> = ({ icon, title, subtitle, count, accent = 'text-intellend-green' }) => (
    <div className="flex items-start gap-3 mb-3">
        <div className="p-2 bg-gray-100 rounded-lg shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`text-sm font-bold ${accent}`}>{title}</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                    {count} {count === 1 ? 'journey' : 'journeys'}
                </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
        </div>
    </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <Users className="w-4 h-4 text-gray-400" />
        </div>
        <p className="text-xs text-gray-400 max-w-xs">{message}</p>
    </div>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────

export const LeadJourneySwapModal: React.FC<LeadJourneySwapModalProps> = ({
    phone,
    tradeName,
    currentLeadId,
    isOpen,
    onClose,
}) => {
    const [data, setData] = useState<LeadJourneySwapData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setData(null);
        setError(null);
        if (!phone) return;
        setLoading(true);
        axios
            .post('/api/public/lead_journey_swap/', { phone })
            .then((res) => setData(res.data as LeadJourneySwapData))
            .catch(() => setError('Failed to load journey data. Please try again.'))
            .finally(() => setLoading(false));
    }, [isOpen, phone]);

    const displayPhone = phone || '-';
    const displayName = tradeName ? `${tradeName} · ` : '';

    const directCount = data?.direct_journeys.length ?? 0;
    const b2pCount = data?.b2p_journeys.length ?? 0;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col p-0 gap-0 rounded-2xl overflow-hidden">
                {/* Header */}
                <DialogHeader className="px-6 py-4 border-b bg-white shrink-0">
                    <DialogTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <ArrowLeftRight className="w-4 h-4 text-intellend-green" />
                        <span>Journey Swap</span>
                        <span className="text-gray-400 font-normal text-sm">
                            — {displayName}
                            <span className="font-mono text-intellend-green">{displayPhone}</span>
                        </span>
                    </DialogTitle>
                    {!loading && !error && data && (
                        <p className="text-xs text-gray-400 mt-0.5">
                            {directCount + b2pCount === 0
                                ? 'No journeys found for this phone number.'
                                : `${directCount + b2pCount} total journey${directCount + b2pCount !== 1 ? 's' : ''} found`}
                        </p>
                    )}
                </DialogHeader>

                {/* Body */}
                <div className="overflow-y-auto flex-1 bg-gray-50/60">
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-20 gap-3">
                            <Loader2 className="w-8 h-8 text-intellend-green animate-spin" />
                            <p className="text-sm text-gray-400">Loading journeys…</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center justify-center py-20">
                            <p className="text-sm text-red-500">{error}</p>
                        </div>
                    )}

                    {!loading && !error && !phone && (
                        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-6">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                <Phone className="w-5 h-5 text-gray-400" />
                            </div>
                            <p className="text-sm font-medium text-gray-500">No phone number</p>
                            <p className="text-xs text-gray-400 max-w-xs">
                                This lead does not have a phone number on record, so journeys cannot be looked up.
                            </p>
                        </div>
                    )}

                    {!loading && !error && data && phone && (
                        <div className="p-5 space-y-6">
                            {/* ── Section 1: Direct Journeys ── */}
                            <div>
                                <SectionHeader
                                    icon={<Phone className="h-4 w-4 text-intellend-green" />}
                                    title="Direct Journeys"
                                    subtitle={`Journeys taken using phone number ${displayPhone}`}
                                    count={directCount}
                                    accent="text-intellend-green"
                                />
                                {directCount === 0 ? (
                                    <EmptyState message={`No journeys found directly linked to ${displayPhone}.`} />
                                ) : (
                                    <div className="space-y-3">
                                            {data.direct_journeys.map((user) => (
                                                <JourneyUserCard key={user.id} user={user} currentLeadId={currentLeadId} highlight />
                                            ))}
                                        </div>
                                )}
                            </div>

                            {/* Divider */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-gray-200" />
                                <div className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                    <Building2 className="h-3 w-3 text-gray-400" />
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                        Business Mapping
                                    </span>
                                </div>
                                <div className="flex-1 h-px bg-gray-200" />
                            </div>

                            {/* ── Section 2: B2P Journeys ── */}
                            <div>
                                <SectionHeader
                                    icon={<Users className="h-4 w-4 text-blue-500" />}
                                    title="Co-applicant Journeys"
                                    subtitle={`Other journeys where ${displayPhone} was part of the same business-to-person mapping`}
                                    count={b2pCount}
                                    accent="text-blue-600"
                                />
                                {b2pCount === 0 ? (
                                    <EmptyState message={`No co-applicant journeys found. ${displayPhone} was not part of any shared business mapping with other phone numbers.`} />
                                ) : (
                                    <div className="space-y-3">
                                            {data.b2p_journeys.map((user) => (
                                                <JourneyUserCard key={user.id} user={user} currentLeadId={currentLeadId} />
                                            ))}
                                        </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LeadJourneySwapModal;
